import {
  Snackbar,
  Alert,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  Divider,
} from "@mui/material";

import { useEffect, useState } from "react";
import axios from "axios.js";
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";

export default function LeadConnectorCard({
  dataFormRef,
  triggerRefresh,
  leadBuckets,
  leadConnector,
  dataFormFields,
  openDialog,
  onClose
}) {
  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [leadFields, setLeadFields] = useState({});
  const [selectedBucket, setSelectedBucket] = useState(null);

  function buildLeadAttributeRows() {
    const fields = [];
    Object.keys(selectedBucket.fields).forEach((code) => {
      fields.push(
        <MDBox mb={3}>
          <MDBox display="flex" justifyContent="space-between">
            <MDBox>
              <MDTypography variant="h6" fontSize={14} fontWeight="light">
                <b>{selectedBucket.fields[code]}</b>
                <br />(
                {["lead_email_address", "lead_phone_number"].includes(code)
                  ? "Optional"
                  : "Required"}
                )
              </MDTypography>
            </MDBox>
            <MDBox>
              <MDTypography variant="h6" fontSize={14} mt={2}>
                <Icon variant="outlined">east</Icon>
              </MDTypography>
            </MDBox>
            <MDBox mt={0.5}>
              <FormControl sx={{ minWidth: 130, width: "100%" }}>
                <InputLabel>Select Field</InputLabel>
                <Select
                  label="Select Field"
                  onChange={(e) => {
                    const temp = leadFields;
                    leadFields[code] = e.target.value;
                    setLeadFields(temp);
                  }}
                  sx={{ padding: "12px", paddingLeft: "3px" }}
                >
                  {
                    // --> Notice below changes
                    dataFormFields.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                      dataFormFields.map((record) => (
                        <MenuItem value={`${record.reference}`}>{record.display_name}</MenuItem>
                      ))
                  }
                </Select>
              </FormControl>
            </MDBox>
          </MDBox>
        </MDBox>
      );
    });
    return fields;
  }

  async function connectLeadBucket() {
    if (selectedBucket === null) {
      setSeverity("error");
      setErrMsg("Please select a lead bucket.");
      setOpen(true);
      return;
    }

    if (leadFields.length < 0) {
      setSeverity("error");
      setErrMsg("Please map lead attributes to your form fields.");
      setOpen(true);
      return;
    }

    let errors = 0;
    Object.keys(selectedBucket.fields).forEach((code) => {
      if (
        (typeof leadFields[code] === "undefined" || leadFields[code] === null) &&
        !["lead_phone_number", "lead_email_address"].includes(code)
      ) {
        setSeverity("error");
        setErrMsg("Map all required attributes.");
        setOpen(true);
        errors += 1;
      }
    });

    if (errors > 0) {
      return;
    }

    if (
      (typeof leadFields.lead_phone_number === "undefined" ||
        leadFields.lead_phone_number === null) &&
      (typeof leadFields.lead_email_address === "undefined" ||
        leadFields.lead_email_address === null)
    ) {
      setSeverity("error");
      setErrMsg("Email address or Phone number is required");
      setOpen(true);
      return;
    }

    onClose(false);
    setShowLoading(true);

    const json = {
      form: dataFormRef,
      bucket: selectedBucket.reference,
      fields: leadFields,
    };

    await axios
      .post("portal/data_forms/leads/connect.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          triggerRefresh();
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function deleteBucket(ref) {
    onClose(false);
    setShowLoading(true);
    await axios
      .get(`/portal/data_forms/leads/${ref}/delete.json`)
      .then((result) => {
        // console.log(result);
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          triggerRefresh();
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  return (
    <>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Dialog open={openDialog} fullWidth maxWidth={leadConnector ? "xs" : "sm"}>
        <DialogTitle>
          <MDBox>Lead Connector</MDBox>
        </DialogTitle>
        <DialogContent>
          {leadConnector !== null ? (
            <MDBox px={1} mb={3}>
              <MDBox display="flex" justifyContent="space-between">
                <MDBox>
                  <MDTypography variant="h6" fontSize={12} fontWeight="light">
                    Lead Bucket
                  </MDTypography>
                  <MDTypography variant="h6" fontSize={16} fontWeight="regular">
                    {leadConnector.bucket}
                  </MDTypography>
                </MDBox>
                <MDBox>
                  <MDButton
                    variant="outlined"
                    color="error"
                    iconOnly
                    size="small"
                    onClick={() => {
                      if (window.confirm("Delete connected lead bucket?") === true) {
                        deleteBucket(leadConnector.reference);
                      }
                    }}
                  >
                    <Icon>delete</Icon>
                  </MDButton>
                </MDBox>
              </MDBox>
            </MDBox>
          ) : (
            <MDBox px={1} mb={5}>
              <MDBox>
                <Alert severity="info">
                  Automate lead generation. Connect lead buckets to data forms.
                </Alert>
              </MDBox>
              <MDBox mt={5}>
                <FormControl sx={{ minWidth: 120, width: "100%" }}>
                  <InputLabel>Select Lead Bucket</InputLabel>
                  <Select
                    label="Select Lead Bucket"
                    onChange={(e) => {
                      leadBuckets.forEach((bucket) => {
                        if (bucket.reference === e.target.value) {
                          setSelectedBucket(bucket);
                        }
                      });
                    }}
                    sx={{ padding: "12px", paddingLeft: "3px" }}
                  >
                    {
                      // --> Notice below changes
                      leadBuckets.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                        leadBuckets.map((record) => (
                          <MenuItem value={`${record.reference}`}>{record.title}</MenuItem>
                        ))
                    }
                  </Select>
                </FormControl>
              </MDBox>
              <MDBox>
                {selectedBucket !== null && (
                  <MDBox mt={4}>
                    <MDTypography
                      pl={0}
                      mb={2}
                      variant="h6"
                      fontSize={16}
                      fontWeight="regular"
                      align="center"
                    >
                      Map lead attributes to form fields.
                    </MDTypography>
                    <MDBox>
                      <Divider />
                    </MDBox>
                    <MDBox display="flex" justifyContent="space-between">
                      <MDBox>
                        <MDTypography variant="h6" fontSize={14} fontWeight="regular">
                          Lead Attribute
                        </MDTypography>
                      </MDBox>
                      <MDBox pr={1}>
                        <MDTypography variant="h6" fontSize={14} fontWeight="regular">
                          Form Field
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                    <MDBox>
                      <Divider />
                    </MDBox>
                    <MDBox>{buildLeadAttributeRows().map((row) => row)}</MDBox>
                  </MDBox>
                )}
              </MDBox>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              onClose(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          {!leadConnector && (
            <MDButton
              color="dark"
              variant="contained"
              onClick={() => {
                connectLeadBucket();
              }}
              startIcon={<Icon>electrical_services</Icon>}
            >
              Connect
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
